<script setup lang="ts">
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import NotificationMark from '@/components/notifications/notification-mark.vue';
import { NavigationMenu } from '@/types';
import { useLeftSectionMenu } from '@/utils/composables/useLeftSectionMenu';
import { featureFlags } from '@/featureFlags';

interface Props {
  activeMenu?: NavigationMenu;
}

withDefaults(defineProps<Props>(), {
  activeMenu: '',
});

const {
  isAdminUser,
  allUserGroupId,
  hasAdminNotification,
  hasUnviewedNotification,
  hasUnviewedInsight,
} = useLeftSectionMenu();
</script>

<template>
  <div class="o-section-container">
    <ul class="o-section-items">
      <li>
        <router-link
          :to="{ name: 'anewsHome' }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'news' }"
          target="_self"
        >
          <DgrIcon name="home" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">ホーム</div>
          <NotificationMark
            v-if="hasUnviewedInsight && activeMenu !== 'news'"
            top="8px"
            right="16px"
          />
        </router-link>
      </li>
      <li>
        <router-link
          :to="{ name: 'mypageProfile' }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'my_page' }"
          target="_self"
        >
          <DgrIcon name="mypage" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">マイページ</div>
        </router-link>
      </li>
      <li>
        <router-link
          :to="{
            name: 'groupActivity',
            params: { groupId: String(allUserGroupId) },
          }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'group' }"
          target="_self"
        >
          <DgrIcon name="users" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">グループ</div>
        </router-link>
      </li>
      <li>
        <router-link
          :to="{
            name: 'surveyTop',
          }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'survey' }"
          target="_self"
        >
          <DgrIcon
            name="sparkles-fill"
            :keep-fill="false"
            class="o-section-icon"
          />
          <div class="o-section-text">要約</div>
        </router-link>
      </li>
      <li>
        <router-link
          :to="{ name: 'mentionsNotifications' }"
          class="o-section o-section-item"
          :class="{ active: activeMenu === 'notifications' }"
          target="_self"
        >
          <DgrIcon name="bell" :keep-fill="false" class="o-section-icon" />
          <div class="o-section-text">お知らせ</div>
          <NotificationMark
            v-if="hasUnviewedNotification"
            top="8px"
            right="16px"
          />
        </router-link>
      </li>
      <template v-if="!featureFlags.ANDEV_10131_CAPABILITY">
        <li v-if="isAdminUser" class="o-section-bottom">
          <router-link
            :to="{
              name: 'contracts',
            }"
            class="o-section o-section-item"
            :class="{ active: activeMenu === 'admin' }"
            target="_self"
          >
            <DgrIcon name="cog" :keep-fill="false" class="o-section-icon" />
            <div class="o-section-text">管理・設定</div>
            <NotificationMark
              v-if="hasAdminNotification"
              right="8px"
              top="8px"
            />
          </router-link>
        </li>
        <li v-else class="o-section-bottom">
          <router-link
            :to="{
              name: 'settingsOrganizationTags',
            }"
            class="o-section o-section-item"
            :class="{ active: activeMenu === 'settings' }"
            target="_self"
          >
            <DgrIcon name="cog" :keep-fill="false" class="o-section-icon" />
            <div class="o-section-text">設定</div>
          </router-link>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.o-section-container {
  position: static;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 20px 8px;
  box-sizing: border-box;
}

ul.o-section-items {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  list-style: none;

  :hover.o-section-item {
    background-color: $color-gray200;
  }
  :hover.o-section-item.active {
    background: $color-green100;
  }

  li.o-section-bottom {
    margin-top: auto;
  }
}

.o-section {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 58px;
  border-radius: 4px;
  margin-bottom: 8px;
  &.active {
    background: $color-green100;
  }
}

.o-section-text {
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 10px;
  text-align: center;
  margin-top: 6px;
}

/** 親要素がactiveのclassを持つ場合子要素をあわせてスタイルをハイライトする */
.o-section-item.active > .o-section-icon {
  color: $color-green600;
}
.o-section-item.active > .o-section-text {
  color: $color-green600;
}
</style>
